/**
 * Pareto Analysis tool runner.
 * Identifies the top ~20% of items that generate ~80% of the value.
 */

import { readToolSettings } from "../lib/toolSettings";
import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Pareto Analysis.
 */
export function getParetoAnalysisSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("pareto-analysis");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * How many of the leading items (sorted by contribution, highest first) are
 * the vital few: the fewest that together carry `1 - threshold` of the total
 * value — 80% with the default 0.2 band (roadmap S-38, owner 2026-10-28: the
 * vital few follow how the value splits, not a fixed share of the items). One
 * very large item can be the vital few alone; ten equal items need eight.
 * Nothing is vital when there is no value at all.
 */
export function paretoVitalFewCount(sortedContributions: readonly number[], threshold: number): number {
  const total = sortedContributions.reduce((sum, v) => sum + (v > 0 ? v : 0), 0);
  if (total <= 0) return 0;
  const target = (1 - threshold) * total;
  let cumulative = 0;
  for (let i = 0; i < sortedContributions.length; i++) {
    cumulative += Math.max(0, sortedContributions[i]);
    // Float sums of shares (0.1 + 0.7) land a hair under the target.
    if (cumulative >= target - 1e-9) return i + 1;
  }
  return sortedContributions.length;
}

/**
 * Runs the Pareto Analysis tool.
 * 
 * @param items - Items to analyze
 * @param _criteriaSet - CriteriaSet (not used, but can contain contribution criterion)
 * @param toolConfig - Tool configuration (pareto_threshold, allow_recursive)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runParetoAnalysisTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `pareto-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get config values
  const { paretoThreshold } = readToolSettings(toolConfig);

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  const analyzedItems: Array<{
    item_id: string;
    contribution: number;
    is_top_20_percent: boolean;
    rank: number;
  }> = [];
  const itemsWithContribution: Array<{ item: Item; contribution: number }> = [];

  for (const item of filteredItems) {
    const contribution = item.attributes.contribution;

    // Validate required attribute
    if (contribution === null || contribution === undefined) {
      continue; // Skip invalid items
    }

    const contributionNum = typeof contribution === "number" ? contribution : Number(contribution);

    if (isNaN(contributionNum) || contributionNum < 0) {
      continue;
    }

    itemsWithContribution.push({ item, contribution: contributionNum });
  }

  // Sort by contribution (descending), then by label (ascending) for tie-breaking
  itemsWithContribution.sort((a, b) => {
    if (b.contribution !== a.contribution) {
      return b.contribution - a.contribution;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // The vital few: the fewest leading items that carry 1 - threshold of the value.
  const topN = paretoVitalFewCount(itemsWithContribution.map((x) => x.contribution), paretoThreshold);

  // Assign ranks and build output
  let currentRank = 1;
  for (let i = 0; i < itemsWithContribution.length; i++) {
    const { item, contribution } = itemsWithContribution[i];

    // If contribution differs from previous, update rank
    if (i > 0 && itemsWithContribution[i - 1].contribution !== contribution) {
      currentRank = i + 1;
    }

    const isTop20Percent = i < topN;

    analyzedItems.push({
      item_id: item.id,
      contribution: contribution,
      is_top_20_percent: isTop20Percent,
      rank: currentRank,
    });
  }

  return {
    items: analyzedItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      valid_items: analyzedItems.length,
      pareto_threshold_used: paretoThreshold,
      top_n_count: topN,
    },
  };
}


